import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  model,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent,
  DateOptions,
  FormComponent, InputComponent, InputOptions,
  ModalComponent,
  ModalOptions,
} from '@shared/components';
import { DropdownComponent, DropdownOptions } from '@shared/components/dropdown/dropdown.component';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { HttpHeaders } from '@angular/common/http';
import { NgOptimizedImage } from '@angular/common';
import { APIService } from '@core/services';
import { PatientService } from '@core/services/patient.service';
import {HTTPMethods, InputTypes, ModalTypes} from '@core/enums';
import { Coding } from '@core/classes/other/coding';
import { DefaultSearchComponent } from '@shared/components/dropdown/search/default-search/default-search.component';
import { LabReport } from '@core/classes/tables/lab-report';
import { assets } from '@core/constants';
import { TranslatePipe } from '@ngx-translate/core';
import moment from 'moment/moment';
import {checkDate} from '@core/helpers';
import { decodeToken } from '@core/helpers/util';

@Component({
  selector: 'ncp-add-lab-report',
  imports: [
    ButtonComponent,
    ModalComponent,
    ReactiveFormsModule,
    DropdownComponent,
    DateComponent,
    NgOptimizedImage,
    TranslatePipe,
    InputComponent,
  ],
  templateUrl: './add-lab-report.component.html',
  styleUrls: ['./add-lab-report.component.scss'],
})
export class AddLabReportComponent implements OnInit {
  @ViewChild('mod')
  public mod!: ModalComponent;

  @ViewChild('frm')
  protected formEl?: NgForm;

  @ViewChild('addLabReportButton')
  public addLabReportButton!: ButtonComponent;

  @ViewChild('labDD')
  public labDD!: DropdownComponent<any>;

  @ViewChild('specialtyDD')
  public specialtyDD!: DropdownComponent<Coding>;

  @ViewChild('onsetDate')
  public onsetDate!: DateComponent;

  @ViewChild('documentFileInput')
  protected documentFileInput?: ElementRef<HTMLInputElement>;

  protected apiService = inject(APIService);
  protected patientService = inject(PatientService);
  private cdref = inject(ChangeDetectorRef);

  protected data = model<any>();
  protected readonly assets = assets;
  protected readonly documentType = '11502-2';

  protected documentContent: string | null = null;
  protected documentFileName: string | null = null;
  protected documentMimeType: string | null = null;

  protected labsList = [
    {
      id: 100001,
      name: 'A.P CENTRALAB DIAGNOSTIC CENTER LTD',
      tel: '26949978',
      email: 'info@centralabdiagnostic.com',
      street: 'Kinira 30',
      postal_code: '8011',
      city: 'Paphos',
      country: 'CY',
    },
    {
      id: 100002,
      name: 'BIOANALYSIS CLINICAL LAB S.C.I.N.A.',
      tel: '25726252',
      email: 'info@bioanalysis.com.cy',
      street: 'Spyrou Kyprianou Avenue 23C',
      postal_code: '4001',
      city: 'Limassol',
      country: 'CY',
    },
    {
      id: 100003,
      name: 'ΧΗΜΕΙΟ ΓΙΑΝΝΟΥΚΑ - ΟΜΙΛΟΣ ΒΙΟΙΑΤΡΙΚΗ',
      tel: '22419700',
      email: 'info@yiannouka.com.cy',
      street: 'Ifigenias 59',
      postal_code: '2003',
      city: 'Strovolos, Nicosia',
      country: 'CY',
    },
    {
      id: 100004,
      name: 'ΜΕΛΑΘΡΟΝ ΑΓΩΝΙΣΤΩΝ ΤΗΣ ΕΟΚΑ',
      tel: '25872287',
      email: 'info@melathroneoka.com.cy',
      street: 'Eleftherias 17',
      postal_code: '4549',
      city: 'Palodia, Limassol',
      country: 'CY',
    },
    {
      id: 100005,
      name: 'ΙΔΙΩΤΙΚΟ ΝΟΣΟΚΟΜΕΙΟ ΕΥΑΓΓΕΛΙΣΜΟΣ',
      tel: '26848000',
      email: 'info@evangelismos.com.cy',
      street: 'Vasileos Constantinou XIII 87',
      postal_code: '8021',
      city: 'Paphos',
      country: 'CY',
    },
    {
      id: 100006,
      name: 'MEDICOVER LABORATORIES CYPRUS LTD',
      tel: '22751333',
      email: 'cyprus@medicover.com',
      street: 'Piraeus 36',
      postal_code: '2023',
      city: 'Strovolos, Nicosia',
      country: 'CY',
    },
    {
      id: 100007,
      name: 'ΑΡΕΤΑΙΕΙΟΝ ΙΔΙΩΤΙΚΟ ΝΟΣΟΚΟΜΕΙΟ',
      tel: '22200300',
      email: 'info@aretaeio.com',
      street: 'Andrea Avraamides 55-57',
      postal_code: '2024',
      city: 'Strovolos, Nicosia',
      country: 'CY',
    },
  ];

  protected form: FormGroup = new FormGroup({
    organizationId: new FormControl<number | undefined>(undefined, [Validators.required]),
    author: new FormControl<string>('', [Validators.required]),
    specialty: new FormControl<number | undefined>(undefined, [Validators.required]),
    onsetDate: new FormControl<string>('', [Validators.required]),
    file: new FormControl<string>('', [Validators.required]),
  }, {
    validators: [this.formValidator()]
  });

  get file () {
    return this.form.get('file') as FormControl;
  }

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let organizationId = (control as FormGroup).controls['organizationId'];
      let author = (control as FormGroup).controls['author'];
      let specialty = (control as FormGroup).controls['specialty'];
      let onsetDate = (control as FormGroup).controls['onsetDate'];
      let file = (control as FormGroup).controls['file'];

      let patientDOB = moment(this.patientService.data?.dob, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'], true);
      let today = moment();

      if(organizationId.value === undefined || organizationId.value === null) {
        organizationId.setErrors({orcd_labInvalid: true});
        errors['orcd_labInvalid'] = true;
      }

      if(author.value === undefined || author.value === null || author.value === '') {
        author.setErrors({orcd_authorInvalid: true});
        errors['orcd_authorInvalid'] = true;
      }

      if(specialty.value === undefined || specialty.value === null) {
        specialty.setErrors({orcd_specialtyInvalid: true});
        errors['orcd_specialtyInvalid'] = true;
      }

      if((!checkDate(onsetDate.value).isValid())) {
        onsetDate.setErrors({da_onSetDateInvalid: true});
        errors['orcd_onSetDateInvalid'] = true;
      } else {
        if(checkDate(onsetDate.value).isBefore(patientDOB)) {
          onsetDate.setErrors({da_onSetDateAfterDOB: true});
          errors['orcd_onSetDateAfterDOB'] = true;
        } else if(checkDate(onsetDate.value).isSameOrAfter(today)) {
          onsetDate.setErrors({da_onSetDateBeforeToday: true});
          errors['orcd_onSetDateBeforeToday'] = true;
        } else {
          onsetDate.setErrors(null);
        }
      }

      if(file.value === undefined || file.value === null || file.value === '') {
        file.setErrors({orcd_fileInvalid: true});
        errors['orcd_hfileInvalid'] = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  protected btnOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'add_lab_report',
      tooltip: 'add_lab_report',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'add_lab_report_modal',
      title: 'add_lab_report',
      type: ModalTypes.form,
      form: FormComponent,
      options: {
        animation: true,
        size: 'xl',
        fullscreen: 'lg',
        backdrop: 'static',
        keyboard: false,
      },
      //disableOnSave: false,
    })
  );

  protected labOptions = signal<DropdownOptions<any>>(
    new DropdownOptions<any>({
      id: 'labReportOrganization',
      name: 'labReportOrganization',
      placeholder: 'select_laboratory',
      label: {
        id: 'lab_report_organization_label',
        text: 'laboratory',
      },
      data: this.labsList,
      dataId: 'id',
      descriptionKey: 'name',
      required: true,
      formControl: this.form.get('organizationId') as FormControl,
    })
  );

  protected authorInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'author_full_name',
      label: {
        text: 'author_full_name',
      },
      type: InputTypes.text,
      name: 'author_full_name',
      formControl: this.form.get('author') as FormControl,
      initialValue: '',
      required: true,
    })
  )

  protected specialtyOptions = signal<DropdownOptions<Coding>>(
    new DropdownOptions<Coding>({
      id: 'labReportSpecialty',
      name: 'labReportSpecialty',
      placeholder: 'specialty',
      label: {
        id: 'lab_report_specialty_label',
        text: 'specialty',
      },
      api: {
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.101',
          description: '',
        },
      },
      required: true,
      searchComponent: DefaultSearchComponent,
      formControl: this.form.get('specialty') as FormControl,
    })
  );

  protected dateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'labReportOnsetDate',
      label: {
        text: 'on_set',
      },
      name: 'labReportOnsetDate',
      initialValue: '',
      maxDate: moment().format('YYYY-MM-DD'),
      required: true,
    })
  );

  ngOnInit(): void {
    this.btnOptions().onClick = () => {
      this.mod.options().onReset = this.onFormReset;
      this.addLabReportButton.blur();
      this.mod.open();
    };

    this.dateOptions().formControl = this.form.get('onsetDate') as FormControl;
    this.modOptions().onSave = this.onSave;
    this.modOptions().onClose = this.onModalClose;
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      this.form.controls[control].markAsTouched();
    })

    return true;
  }

  protected onSave = (): void => {
    // if (this.modOptions().disableOnSave) {
    //   return;
    // }
    //
    // this.modOptions().disableOnSave = true;
    // this.mod.detectChanges();
    this.formEl?.ngSubmit.emit();
  };

  protected onFormReset = (): void => {
    // if (this.modOptions().disableOnSave) {
    //   return;
    // }

    //this.modOptions().disableOnSave = true;
    //this.mod.detectChanges();
    this.resetForm();

    // setTimeout(() => {
    //   this.modOptions().disableOnSave = false;
    //   this.mod.detectChanges();
    // });
  };

  protected onModalClose = (): void => {
    this.resetForm();
    //this.modOptions().disableOnSave = false;
    this.mod.detectChanges();
  };

  private resetForm = (): void => {
    this.form.reset({
      organizationId: undefined,
      author: '',
      specialty: undefined,
      onsetDate: '',
      file: ''
    });

    this.documentContent = null;
    this.documentFileName = null;
    this.documentMimeType = null;

    if (this.documentFileInput) {
      this.documentFileInput.nativeElement.value = '';
    }

    this.labDD?.clearSelection();
    this.specialtyDD?.clearSelection();

    this.modOptions().errors = [];

    this.cdref.detectChanges();
  };

  protected onFileSelected = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      this.documentContent = null;
      this.documentFileName = null;
      this.documentMimeType = null;
      this.cdref.detectChanges();
      return;
    }

    if (file.type !== 'application/pdf') {
      input.value = '';
      this.documentContent = null;
      this.documentFileName = null;
      this.documentMimeType = null;
      this.cdref.detectChanges();
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result?.toString() || '';
      this.documentContent = result.split(',')[1] || null;
      this.documentFileName = file.name;
      this.documentMimeType = file.type;
      this.cdref.detectChanges();
    };

    reader.onerror = () => {
      input.value = '';
      this.documentContent = null;
      this.documentFileName = null;
      this.documentMimeType = null;
      this.cdref.detectChanges();
    };

    reader.readAsDataURL(file);
  };

  protected onFormSubmit = (): void => {
    console.log(this.form.value || !this.documentContent);
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    const lab = this.labsList.find((l) => l.id === this.form.get('organizationId')?.value);

    if (!lab) {
      //this.modOptions().disableOnSave = false;
      //this.mod.detectChanges();
      return;
    }

    const hcp = this.getHcp();
    const onsetDateValue = this.form.get('onsetDate')?.value;

    const payload: any = {
      status: 1,
      documentType: this.documentType,
      document: this.documentContent,
      onsetDate: this.formatDateForOrcd(onsetDateValue),
      hcpName: hcp.hcpName,
      hcpSurname: hcp.hcpSurname,
      author: this.form.get('author')?.value,
      specialty: this.specialtyOptions().value?.description,
      organizationId: lab.id.toString(),
      organizationName: lab.name,
      organizationTel: lab.tel,
      organizationEmail: lab.email,
      organizationStreet: lab.street,
      organizationPostalCode: lab.postal_code,
      organizationCity: lab.city,
      organizationCountry: lab.country,
    };

    this.apiService
      .call({
        path: '/document',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        type: HTTPMethods.POST,
        body: payload,
      })
      ?.subscribe({
        next: (res: any) => {
          if (res && res.status?.toString() === '1') {
            const tableData = this.data()?.tblOptions().data || [];

            const row: LabReport = res.data || {
              id: res.id,
              orcd_document_id: res.orcd_document_id,
              onset_date: this.formatDateForTable(onsetDateValue),
              hcp_name: hcp.hcpName,
              hcp_surname: hcp.hcpSurname,
              organization_name: lab.name,
              status: 1,
              document_type: this.documentType,
              preview: '',
            };

            tableData.unshift(row);

            if (this.data()?.tbl?.tableData) {
              this.data().tbl.tableData = tableData;
            }

            //this.modOptions().disableOnSave = false;
            this.mod.detectChanges();
            this.mod.close();
            return;
          }

          //this.modOptions().disableOnSave = false;
          this.mod.detectChanges();
        },
        error: () => {
          //this.modOptions().disableOnSave = false;
          this.mod.detectChanges();
        },
      });
  };

  private formatDateForOrcd = (value?: string | Date | null): string | null => {
    if (!value) {
      return null;
    }

    if (typeof value === 'string' && value.includes('-')) {
      const parts = value.split('-');

      if (parts[0].length === 4) {
        return `${parts[0]}-${parts[1]}-${parts[2]} 00:00:00`;
      }

      if (parts[2]?.length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]} 00:00:00`;
      }
    }

    const date = new Date(value);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day} 00:00:00`;
  };

  private formatDateForTable = (value?: string | Date | null): string => {
    if (!value) {
      return '';
    }

    if (typeof value === 'string' && value.includes('-')) {
      const parts = value.split('-');

      if (parts[0].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }

      if (parts[2]?.length === 4) {
        return value;
      }
    }

    const date = new Date(value);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  private getHcp = (): {
    hcpName: string;
    hcpSurname: string;
  } => {
    const profile = JSON.parse(sessionStorage.getItem('profile') || '{}');
    const currentUser = JSON.parse(sessionStorage.getItem('currentUser') || '{}');
    const token = profile?.jwt || profile?.token || currentUser?.token;
    const decoded = this.decodeJwt(token);

    return {
      hcpName: decoded?.given_name || profile?.given_name || '',
      hcpSurname: decoded?.family_name || profile?.family_name || '',
    };
  };

  private decodeJwt = (token?: string): any => {
    return token ? decodeToken(token) : {};
  };
}
